import type { Step } from "@/components/instruction/InstructionKit";
import {
  MockChatsCollect,
  MockFeedStack,
  MockVacancyOpen,
  MockGenReply,
  MockVacancyFiles,
  MockReviews,
  MockRefresh,
  MockSubRow,
  MockAboutYou,
  MockAddGroups,
  MockSpheresRow,
  MockSourcesRow,
  MockWorkFormatRow,
  MockBirthdayRow,
  MockFormatRow,
  MockKB,
  MockCourse,
  MockQuest,
  MockReferral,
  MockReferralExchange,
  MockBotMessage,
} from "@/components/instruction/InstructionKit";

/**
 * Контент усіх інструкцій. Анімовані емодзі — лише з робочого набору,
 * БЕЗ 💬 і 🚀 (їх Lottie забагований).
 */

// Базова інструкція (5-7 кроків) — одразу після оформлення підписки.
export const BASIC_STEPS: Step[] = [
  {
    emoji: "🧡",
    title: "Вітаємо!",
    body: "Ти оформив підписку — тепер inFLOW працює для тебе на повну. За хвилину покажемо, як тут усе влаштовано.",
    hero: true,
  },
  {
    emoji: "💎",
    title: "Що відкрилось",
    body: "Підписка відкрила все: безлімітні контакти, AI-відповіді замовнику, база знань і курс — щоб ти швидше знаходив клієнтів.",
  },
  {
    emoji: "📨",
    title: "Стрічка вакансій",
    body: "Замовники щодня пишуть у чатах «шукаю фахівця». inFLOW збирає ці вакансії й миттєво приносить тобі — в застосунок і в бот.",
    mock: <MockChatsCollect />,
  },
  {
    emoji: "⚡",
    title: "Вакансії у боті",
    body: "Нові вакансії приходять прямо в бот окремими повідомленнями. Одразу генеруєш AI-відповідь і пишеш клієнту.",
    mock: <MockBotMessage />,
  },
  {
    emoji: "👀",
    title: "Підкажемо на місці",
    body: "Коли вперше зайдеш у будь-який розділ — покажемо коротку підказку прямо там. Так розберешся в усьому в процесі.",
  },
  {
    emoji: "🎉",
    title: "Готово!",
    body: "Це все, що треба для старту. Тисни «Почати користуватись» — і вперед, по вакансії!",
  },
];

// Контекстні підказки розділів (1-3 кроки), показуються 1 раз при вході.
export const SECTION_STEPS: Record<string, Step[]> = {
  feed: [
    {
      emoji: "📨",
      title: "Твоя стрічка",
      body: "Тут збираються свіжі вакансії за твоїми сферами й джерелами. Найновіші — зверху. Під шапкою картки — факти з тексту: місто, досвід і оплата, якщо замовник їх указав.",
      mock: <MockFeedStack />,
    },
    {
      emoji: "👇",
      title: "Оновити й шукати",
      body: "Вгорі стрічки — кнопки оновлення та пошуку. Оновлення підтягує найсвіжіше, пошук шукає по всій базі за півроку.",
      mock: <MockRefresh />,
    },
    {
      emoji: "🔑",
      title: "Відкрий вакансію",
      body: "Тапни на вакансію — і побачиш її повний текст із контактами замовника.",
      mock: <MockVacancyOpen />,
    },
    {
      emoji: "📝",
      title: "Згенерувати відповідь",
      body: "AI напише відповідь на основі твого профілю строго за промптом. Три промпти вже готові — їх можна читати, міняти й видаляти, а ще додати до п'яти своїх. Мову відповіді обираєш сам: українська, англійська або російська.",
      mock: <MockGenReply />,
    },
    {
      emoji: "📎",
      title: "Файли від замовника",
      body: "Якщо до вакансії прикріплені бриф, фото чи відео — вони під текстом, у блоці «Додаткові матеріали». Документи й таблиці відкриваються прямо тут.",
      mock: <MockVacancyFiles />,
    },
    {
      emoji: "✅",
      title: "Відгуки про замовника",
      body: "У вакансіях з Telegram-чатів видно рейтинг і відгуки про замовника. А ще ти сам можеш залишити відгук.",
      mock: <MockReviews />,
    },
  ],
  profile: [
    {
      emoji: "💎",
      title: "Управління підпискою",
      body: "Їдеш у відпустку — заморозь підписку, щоб не згоряли оплачені дні. Тут же продовжуєш її наперед.",
      mock: <MockSubRow />,
    },
    {
      emoji: "📝",
      title: "Про тебе",
      body: "Чим детальніше опишеш досвід і послуги — тим влучніші відповіді генеруватиме AI. Заповнюєш один раз.",
      mock: <MockAboutYou />,
    },
    {
      emoji: "✅",
      title: "Сфери",
      body: "Познач лише свої напрями — і чужі вакансії не потраплятимуть у стрічку. Змінити можна будь-коли.",
      mock: <MockSpheresRow />,
    },
    {
      emoji: "👀",
      title: "Джерела",
      body: "Якщо з якогось джерела забагато зайвого — просто вимкни його, решта працюватиме далі.",
      mock: <MockSourcesRow />,
    },
    {
      emoji: "🧩",
      title: "Вакансії чи замовлення",
      body: "Шукаєш роботу в компанії — лиши «Вакансії». Береш разові проєкти — лиши «Замовлення».",
    },
    {
      emoji: "💻",
      title: "Формат вакансій",
      body: "Працюєш лише віддалено? Лиши «Онлайн» — і офісні вакансії не відволікатимуть.",
      mock: <MockWorkFormatRow />,
    },
    {
      emoji: "📨",
      title: "Оформлення вакансій",
      body: "Не любиш довгі полотна — увімкни короткий AI-переказ: суть вакансії за пару рядків.",
      mock: <MockFormatRow />,
    },
    {
      emoji: "👥",
      title: "Додати свої групи",
      body: "Є свій чат чи канал із вакансіями, якого немає в inFLOW? Додай — бот стежитиме за ним лише для тебе.",
      mock: <MockAddGroups />,
    },
    {
      emoji: "🤖",
      title: "Генерація відповідей",
      body: "Промпти, за якими AI пише відгуки на вакансії. Тут їх зручно налаштувати наперед, не відкриваючи вакансію.",
      mock: <MockGenReply />,
    },
    {
      emoji: "🎂",
      title: "День народження",
      body: "У свій день народження ми приготуємо тобі приємний подарунок-сюрприз. Головне — вкажи дату заздалегідь. Змінити її потім можна лише один раз.",
      mock: <MockBirthdayRow />,
    },
  ],
  kb: [
    {
      emoji: "📚",
      title: "База знань",
      body: "Статті, подкасти та уроки від спікерів про фріланс і пошук клієнтів — усе в одному місці.",
      mock: <MockKB />,
    },
  ],
  quests: [
    {
      emoji: "💰",
      title: "Квести",
      body: "Виконуй завдання й отримуй бонуси — ними оплачуєш до 50% вартості підписки.",
      mock: <MockQuest />,
    },
  ],
  referrals: [
    {
      emoji: "🤝",
      title: "Реферали",
      body: "Запрошуй друзів за своїм посиланням і заробляй на кожному — за реєстрацію, першу підписку та всі продовження.",
      mock: <MockReferral />,
    },
    {
      // Owner 2026-07-29: обмін — головний сценарій витрати реф-грошей,
      // у тексті про заробіток він губився, тому окремий крок.
      emoji: "💎",
      title: "Обмін або вивід",
      // Owner 2026-10-19: при виводі людина вводить номер картки в застосунку,
      // а коли гроші надіслано — бот пише їй про це.
      body: "Зароблені гроші можна обміняти на дні підписки — за реферальним тарифом це дешевше, ніж купувати, а решта балансу не згорає. Або вивести на картку від 100 ₴: вводиш номер картки, і щойно гроші надішлемо, у боті прийде повідомлення.",
      mock: <MockReferralExchange />,
    },
  ],
  course: [
    {
      emoji: "🎬",
      title: "Курс",
      body: "Відеокурс від засновника: як знаходити клієнтів і будувати стабільний потік замовлень.",
      mock: <MockCourse />,
    },
  ],
};
